import { action, computed, observable } from "mobx";
import { message } from "antd";
import moment from "moment";
import AbstractLoaderStore from "./AbstractLoaderStore";
import {
  IObjectMap,
  Row,
  UpdateCustomGroupDataValue,
  UpdateDeclarantDataKeys,
  UpdateDeclarantDataValue,
  UpdateDeclarantKeys,
  UpdateDeclarantValue,
} from "../../interfaces/common";
import { Declarant } from "../../models/Declarant";
import ee from "../../services/EventEmitter";
import {
  CHANGE_CUSTOM_GROUP_FIELD,
  CHANGE_DEPARTMENTS_DATA_FIELD,
  CHANGE_EXPOSED_FIELD_IN_DEPARTMENTS_DATA,
  CHANGE_EXPOSED_FIELD_IN_DEPARTMENTS_DATA_GROUP,
  CHANGE_WARRANTY_CONVERT_IN_DEPARTMENTS_DATA,
} from "../../constants/events";
import { defaultDateRange } from "../../components/declarants/dateRange";
import {
  DepartmentsSummaryObject,
  IDepartmentsSummary,
} from "../../components/declarants/Interfaces";
import { Client } from "../../models/ByClients/Client";
import getFileNameFromContentDisposition from "../../helpers/parsers";
import downloadFile from "../../helpers/downloadFile";

import isNaN from "../../helpers/number";
import findCustomGroupById from "../../utils/findCustomGroupById";
import findSelectedEntities from "../../utils/findSelectedEntities";
import updateGroupedItems from "../../helpers/updateGroupedItems";
import { Item } from "../../models/DepartmentData/Item";
import { DepartmentDataCustomGroupAttributes } from "../../models/DepartmentDataCustomGroupAttributes";
import { isCustomGroupKey } from "../../helpers/customGroup";
import {
  getConsignmentItemsSum,
  getCustomGroupTotalCost,
  getWarrantySum,
} from "../../helpers/departmentData";
import { ChangeFieldEvent } from "../../components/tables/byClientsTable/columns/interfaces";
import { Option } from "../../components/common/forms/Interfaces";
import {
  DepartmentSource,
  departmentSources,
  departmentSourcesMap,
} from "../../models/DepartmentData/DepartmentSource";
import { GenerateInvoiceValuesObject } from "../../components/modals/GenerateInvoiceModal/types";
import { getInvoiceDiscountParameters } from "../../components/modals/GenerateInvoiceModal/discount";
import { getInvoiceGenerationErrorMessage } from "../../helpers/invoiceGenerationError";
import { OptionMap } from "../../helpers/optionMap";
import {
  CustomDepartmentSummaryTitlesMap, DepartmentSummaryField,
  departmentSummaryFields,
  DepartmentSummaryResponse
} from "../../models/DepartmentData/DepartmentSummary";
import {Nullable} from "../../models/Common";
import {DEPARTMENT_DATA_TABLE_COLUMNS} from "../../constants/translates";
import {ServiceCatalogEntry} from "../../models/DepartmentData/ServiceCatalogEntry";

export interface DeclarantInvoiceType {
  key: string;
  label: string;
}

const getSummaryTitle = (key: DepartmentSummaryField): string => {
  return CustomDepartmentSummaryTitlesMap.get(key) || DEPARTMENT_DATA_TABLE_COLUMNS[key] || 'Unnamed';
};

const defaultParameters = {
  ...defaultDateRange,
  page: 0,
};

const defaultSummary: DepartmentsSummaryObject = departmentSummaryFields.reduce((acc: object, field) => {
  return {
    ...acc,
    [field]: {
      title: getSummaryTitle(field),
      value: 0,
    },
  };
}, {});

class DepartmentsDataStore
  extends AbstractLoaderStore
  implements IDepartmentsSummary {
  constructor() {
    super();

    ee.on(CHANGE_DEPARTMENTS_DATA_FIELD, (value: UpdateDeclarantDataValue) => {
      this.updateEntity(value.entityId, value.entityKey, value.value);
    });

    ee.on(CHANGE_CUSTOM_GROUP_FIELD, async (value: UpdateCustomGroupDataValue) => {
      void this.executeWithSummaryLoading(async () => {
        await this.updateCustomGroup(
          value.entityId.toString().replace("custom_group-", ""),
          value.entityKey,
          value.value
        );
        await this.loadSummary();
      });
    });

    ee.on(
      CHANGE_EXPOSED_FIELD_IN_DEPARTMENTS_DATA,
      (value: UpdateDeclarantValue) => {
        this.updateExposedField(
          value.entityId,
          value.entityKey as UpdateDeclarantKeys,
          value.value
        );
      }
    );

    ee.on(
      CHANGE_EXPOSED_FIELD_IN_DEPARTMENTS_DATA_GROUP,
      (value: ChangeFieldEvent) => {
        this.updateExposedFieldInGroup(
          value.groupId,
          value.declarantIds,
          value.key as UpdateDeclarantKeys,
          value.value
        );
      }
    );

    ee.on(
      CHANGE_WARRANTY_CONVERT_IN_DEPARTMENTS_DATA,
      (value: UpdateDeclarantValue) => {
        this.updateWarrantyConvert(value.entityId, value.value);
      }
    );

    this._parameters = {
      ...defaultParameters,
      source: this.defaultSource,
    };
  }

  sourceMap: OptionMap<DepartmentSource> = departmentSourcesMap;

  @observable
  summaryLoading: boolean = false;

  @observable
  summary: DepartmentsSummaryObject = {...defaultSummary};

  @observable
  selectedSummary: DepartmentsSummaryObject = {...defaultSummary};

  @observable
  customGroupsEntities: Declarant[] = [];

  @observable
  customGroupsAttributes: DepartmentDataCustomGroupAttributes[] = [];

  @observable
  serviceCatalog: ServiceCatalogEntry[] | null = null;

  private serviceCatalogFetch: Promise<ServiceCatalogEntry[]> | null = null;

  @observable
  selectedRows: Row[] = [];

  @observable
  expandedRows: Row[] = [];

  @observable
  customExportButtonAvailability = false;

  get sourceOptions(): Option<DepartmentSource>[] {
    return this.sourceMap.getOptions();
  }

  // eslint-disable-next-line class-methods-use-this
  get defaultSource(): DepartmentSource {
    return departmentSources[0];
  }

  @computed
  get isDisabledGroupButton(): boolean {
    if (this.selectedRows.length) {
      const keys = this.customGroupsEntities.map((entity) => entity.id);

      return this.selectedRows.some((item) => keys.includes(item));
    }

    return true;
  }

  @computed
  get isDisabledUngroupButton(): boolean {
    if (this.selectedRows.length) {
      const keys = this.customGroupsEntities.map((entity) => entity.id);

      return this.selectedRows
        .filter((key) => !isNaN(key))
        .some((item) => !keys.includes(item));
    }

    return true;
  }

  @computed
  get isDisabledJoinButton(): boolean {
    if (this.selectedRows.length === 0) {
      return true;
    }

    const customGroups = this.selectedRows.filter((key) =>
      key.toString().startsWith("custom_group")
    );

    if (customGroups.length > 1) {
      return true;
    }

    const customGroup = findCustomGroupById(
      customGroups[0] as string,
      this._data as Client[]
    );

    if (customGroup) {
      const items = findSelectedEntities(
        this.selectedRows,
        this._data as Client[]
      );

      if (items.length === 0) {
        return true;
      }

      return items.some(
        (item) =>
          item.means_of_transportation !== customGroup.means_of_transportation
      );
    }

    return true;
  }

  @computed
  get isDisabledInvoiceButton(): boolean {
    return !this.selectedRows.some((key) =>
      key.toString().startsWith("custom_group")
    );
  }

  @computed
  get isDisabledRegisterAccountsButton(): boolean {
    return !this.selectedRows.some((key) =>
      key.toString().startsWith("custom_group")
    );
  }

  @computed
  get selectedGroupClient(): Client | null {
    const firstGroupKey = this.selectedRows.find((key) =>
      key.toString().startsWith("custom_group")
    );

    if (!firstGroupKey) return null;

    let result: Client | null = null;

    (this._data as Client[]).forEach((clientRow) => {
      if (!Array.isArray(clientRow.children)) return;

      const hasGroup = clientRow.children.some(
        (child) => child.id === firstGroupKey
      );

      if (hasGroup) {
        result = clientRow;
      }
    });

    return result;
  }

  @computed
  get isDisabledCustomExportButton(): boolean {
    return !this.customExportButtonAvailability;
  }

  @computed
  get totalCostCustomGroups(): number {
    return this.customGroupsAttributes
      .map((entity) => getCustomGroupTotalCost(entity, 6) || 0)
      .reduce((prevCost, currentCost) => prevCost + currentCost, 0);
  }

  @action
  changeSelectedRows(ids: Row[]): void {
    if(ids.length === 0) {
      this.selectedSummary = {...defaultSummary};
    } else if(ids.length >= 1) {
      const items = this.findItemsByIds(ids);

      this.recalculateSelectedSummary(items, ids);
    }
    this.selectedRows = ids;
  }

  async executeWithSummaryLoading<T>(callback: () => Promise<T>): Promise<void> {
    this.summaryLoading = true;
    try {
      await callback();
    } finally {
      this.summaryLoading = false;
    }
  }

  private recalculateSelectedSummary(data: Client[], ids: (string | number)[]): void {
    const values: DepartmentsSummaryObject = {};
    data.forEach((client) => {
      const clientValues = departmentSummaryFields.reduce((acc: object, field) => {
        let clientValue: number = 0;
        if(client.children) {
          client.children.forEach((item) => {
            if(typeof item.id === 'string' && item.id.toString().includes('custom_group') && ids.includes(item.id)) {
              const departmentData = item.department_data as unknown as Nullable<DepartmentDataCustomGroupAttributes>;
              if(departmentData) {
                if(field !== 'total_cost') {
                  if(departmentData[field]) {
                    clientValue += departmentData[field] || 0;
                  }
                } else {
                  clientValue += getCustomGroupTotalCost(departmentData) || 0;
                }
              }
            }
          });
        }
        return {
          ...acc,
          [field]: {
            title: getSummaryTitle(field),
            value: clientValue,
          },
        }
      }, {}) as DepartmentsSummaryObject;
      Object.entries(clientValues).forEach(([key, value]) => {
        const typedKey = key as keyof DepartmentsSummaryObject;
        if(value) {
          values[typedKey] = {
            ...value,
            value: (values[typedKey]?.value || 0) + (value.value || 0),
          }
        }
      });
    });
    this.selectedSummary = {
      ...values,
    };
  }

  private findItemsByIds(ids: (string | number)[]): Client[] {
    return (this.data as Client[]).filter((item: Client) => {
      let shouldInclude = false;
      if(ids.includes((item as { id: string | number }).id)) {
        shouldInclude = true;
      } else {
        if(item.children.some((child) => {
          return typeof child.id === 'string' && child.id.toString().includes('custom_group') && ids.includes(child.id);
        })) {
          shouldInclude = true;
        }
      }
      return shouldInclude;
    }) as Client[];
  };

  getLoadResponse(parameters: IObjectMap): Promise<Response> {
    return this.apiManager.loadByUserDepartment(parameters);
  }

  async loadServices(): Promise<ServiceCatalogEntry[]> {
    if (this.serviceCatalog !== null) {
      return this.serviceCatalog;
    }

    if (this.serviceCatalogFetch !== null) {
      return this.serviceCatalogFetch;
    }

    const serviceCatalogFetch: Promise<ServiceCatalogEntry[]> = this.apiManager.getServices().then(async (response) => {
      const services: ServiceCatalogEntry[] = await response.json();
      this.serviceCatalog = services;
      return services;
    });
    this.serviceCatalogFetch = serviceCatalogFetch;

    try {
      return await serviceCatalogFetch;
    } finally {
      this.serviceCatalogFetch = null;
    }
  }

  load(parameters: IObjectMap | null, needUpdateData?: boolean): void {
    super.load(parameters, needUpdateData);
    this.executeWithSummaryLoading(async () => {
      await this.loadSummary();
    });
    this.customExportButtonAvailability = !!this._parameters.client;
  }

  protected async finishLoading(
    response: Response,
    needUpdateData: boolean
  ): Promise<Response> {
    const result = super.finishLoading(response, needUpdateData);

    result.then(() => {
      const entities: Declarant[] = [];

      (this._data as Client[]).forEach((entity) => {
        if (Array.isArray(entity.children) && entity.children.length > 0) {
          entity.children.forEach((item) => {
            if (Array.isArray(item.children) && item.children.length > 0) {
              entities.push(...item.children);
            }
          });
        }
      });

      if (entities.length > 0) {
        this.customGroupsEntities = entities;
      }

      this.calculateCustomGroupAttributes();
    });

    return response;
  }

  @action
  private updateEntity(
    id: unknown,
    key: UpdateDeclarantDataKeys,
    value: unknown
  ): void {
    const params = {} as { [key: string]: unknown };
    params[key] = value;

    this.apiManager
      .updateDepartmentDataByDeclarant(id, params)
      .then(async (response) => {
        const data = await response.json();

        if (response.ok) {
          const newData = [...this.data] as Client[];

          const filterData = newData.map((item) => {
            return {
              ...item,
              children: item.children.filter((doc) => doc.id !== data.item.id),
            };
          });

          const index = filterData.findIndex(
            (item) => item.key === data.item.client_id
          );

          if (filterData[index] && data.item && key === "client_id") {
            const updateGroup = [...filterData[index].children, ...[data.item]];
            filterData[index] = {
              ...filterData[index],
              children: updateGroup,
            };
            this._data = filterData;
          } else {
            this._data = updateGroupedItems(
              [...this._data],
              id,
              value,
              data.item
            );
          }

          message.success(data.message);

          void this.executeWithSummaryLoading(async () => {
            await this.loadSummary();
          });

          return;
        }

        message.error(data.message);
      });
  }

  async loadSummary(): Promise<void> {
    return this.apiManager
      .loadDepartmentDataSummary(this._parameters as IObjectMap)
      .then(async (response) => {
        const data: DepartmentSummaryResponse = await response.json();

        this.summary = Object.entries(data).reduce((acc: object, [key, value]) => {
          return {
            ...acc,
            [key]: {
              title: getSummaryTitle(key as keyof typeof data),
              value,
            },
          };
        }, {});
      });
  }

  downloadXml(): void {
    this.apiManager
      .sendDownloadRequest(
        this.selectedRows.filter(
          (row) =>
            !row.toString().includes("client") &&
            !row.toString().includes("custom_group")
        )
      )
      .then((response) => {
        const fileName = getFileNameFromContentDisposition(
          response.headers.get("Content-Disposition")
        );

        response.blob().then((blob) => {
          downloadFile(blob, fileName);
        });
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.message);
      });
  }

  downloadExcel(params: IObjectMap): void {
    this.apiManager
      .downloadExcelFile(params)
      .then((response) => {
        const fileName = getFileNameFromContentDisposition(
          response.headers.get("Content-Disposition")
        );

        response.blob().then((blob) => {
          downloadFile(blob, fileName);
        });
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.message);
      });
  }

  downloadCustomExcel(params: IObjectMap): void {
    this.apiManager
      .downloadCustomExcelFile(params)
      .then((response) => {
        const fileName = getFileNameFromContentDisposition(
          response.headers.get("Content-Disposition")
        );

        response.blob().then((blob) => {
          downloadFile(blob, fileName);
        });
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.message);
      });
  }

  async makeCustomGroup(store: DepartmentsDataStore): Promise<void> {
    await super.groupDeclarants(this.selectedRows, store);
  }

  async makeUngroup(store: DepartmentsDataStore): Promise<void> {
    await super.sendUngroupRequest(this.selectedRows, store);
  }

  async joinGroup(): Promise<void> {
    await super.joinEntities(this.selectedRows);
    this.selectedRows = [];
    this.customGroupsEntities = [];
  }

  private getCurrentGroups(): object[] {
    const selectedGroupKeys = this.selectedRows.filter((key) =>
      key.toString().startsWith("custom_group")
    );

    return selectedGroupKeys
      .map((groupKey) => {
        const groupEntity = findCustomGroupById(
          groupKey.toString(),
          this._data as Client[]
        );

        if (!groupEntity) return null;

        const children: Item[] = (groupEntity.children as unknown) as Item[];
        const departmentData = (groupEntity.department_data as unknown) as
          | DepartmentDataCustomGroupAttributes
          | undefined;

        // Compute TNVED (consignment_items display like "5/4")
        let tnved: string;
        if (
          Number(departmentData?.consignment_items_length) &&
          Number(departmentData?.consignment_items)
        ) {
          tnved = `${departmentData?.consignment_items_length}/${departmentData?.consignment_items}`;
        } else {
          const { length } = children;
          const itemsSum = children.reduce(getConsignmentItemsSum, 0) - length;
          tnved = `${length}/${itemsSum}`;
        }

        // Compute warranty
        const warranty = departmentData?.warranty
          ? Number(departmentData.warranty)
          : children.reduce(getWarrantySum, 0);

        const warrantyCost = Number(departmentData?.guarantee_cost || 0);
        const epiCost = Number(departmentData?.epi_cost || 0);
        const totalCost =
          getCustomGroupTotalCost(
            {
              guarantee_cost: warrantyCost,
              epi_cost: epiCost,
              t1: departmentData?.t1,
            },
            6
          ) || 0;
        const t1 = departmentData?.t1 ?? null;

        const declarantIds = children
          .map((child) => child.id)
          .filter((id) => typeof id === "number");

        return {
          group_id: groupEntity.key,
          declarant_ids: declarantIds,
          tnved,
          warranty,
          warranty_cost: warrantyCost,
          epi_cost: epiCost,
          total_cost: totalCost,
          t1,
        };
      })
      .filter(Boolean) as object[];
  };

  private handleDownloadResponse(response: Response, defaultFileName: string = 'file.pdf'): void {
    const fileName = getFileNameFromContentDisposition(
      response.headers.get("Content-Disposition")
    ) || defaultFileName;
    response.blob().then((blob) => {
      downloadFile(blob, fileName);
    });
  };

  async generateInvoice(
    values: GenerateInvoiceValuesObject,
  ): Promise<boolean> {
    const generateInvoiceFieldsMap = new Map<
      string,
      keyof GenerateInvoiceValuesObject
    >([
      ["invoice_type", "invoiceType"],
      ["invoice_date", "invoiceDate"],
      ["invoice_prefix", "invoicePrefix"],
      ["payment_details", "paymentDetails"],
      ["index_number", "indexNumber"],
      ["contract_reference", "contractReference"],
      ["reisas", "reisas"],
      ["guarantor_job_title", "guarantorJobTitle"],
      ["guarantor_name", "guarantorName"],
      ["title", "title"],
    ]);
    // Collect all selected custom_group rows from data
    const groups: object[] = this.getCurrentGroups();

    const generateParameters = (): Object => {
      const result: Record<
        string,
        GenerateInvoiceValuesObject[keyof GenerateInvoiceValuesObject]
      > = {};
      Array.from(generateInvoiceFieldsMap.entries()).forEach(
        ([key, value]: [string, keyof GenerateInvoiceValuesObject]) => {
          result[key] = values[value] || null;
        }
      );
      return result;
    };

    return this.apiManager
      .generateInvoice({
        ...generateParameters(),
        ...getInvoiceDiscountParameters(values.invoiceType, values.discountType, values.discountValue),
        groups,
      })
      .then((response) => {
        this.handleDownloadResponse(response, 'invoice.pdf');
        return true;
      })
      .catch(async (response: unknown): Promise<boolean> => {
        if (!(response instanceof Response)) {
          throw response;
        }
        const data: unknown = await response.json();
        message.error(getInvoiceGenerationErrorMessage(data));
        return false;
      });
  }

  async generateRegisterOfAccounts(): Promise<void> {
    const groups = this.getCurrentGroups();
    const dateFrom = moment(this._parameters.start_date as moment.MomentInput).format("DD.MM.YYYY");
    const dateTo = moment(this._parameters.end_date as moment.MomentInput).format("DD.MM.YYYY");

    return this.apiManager.generateRegisterOfAccounts({ date_from: dateFrom, date_to: dateTo, groups })
      .then((response) => {
        this.handleDownloadResponse(response, 'accounts-register.xlsx');
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.message);
      });
  };

  async downloadInvoice(
    key: number,
    name?: string,
    type?: string
  ): Promise<true | null> {
    try {
      const res: Response = await this.apiManager.downloadInvoice(key, type);
      const filename: string = name || "file";

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      const a: HTMLAnchorElement = document.createElement("a");
      a.href = url;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return true;
    } catch {
      return null;
    }
  }

  async getInvoiceTypes(): Promise<DeclarantInvoiceType[] | null> {
    try {
      const response: Response = await this.apiManager.getDeclarantInvoiceTypes();
      return (await response.json()) as DeclarantInvoiceType[];
    } catch {
      return null;
    }
  }

  @action
  private updateExposedField(
    id: unknown,
    key: UpdateDeclarantKeys,
    value: string | number | null
  ): void {
    const params = {} as { [key: string]: unknown };
    params[key] = value;

    this.apiManager.updateDeclarant(id, params).then(async (response) => {
      const data = await response.json();

      if (response.ok) {
        this._data = updateGroupedItems(
          [...this._data],
          id,
          (value as number) > 0,
          data.item
        );

        message.success(data.message);
        return;
      }

      message.error(data.message);
    });
  }

  @action
  private updateWarrantyConvert(id: unknown, value: unknown): void {
    this.apiManager
      .updateDeclarant(id, { is_warranty_convert: value })
      .then(async (response) => {
        const data = await response.json();

        if (response.ok) {
          this._data = (this._data as Client[]).map((client) => {
            if (
              !Array.isArray(client.children) ||
              client.children.length === 0
            ) {
              return client;
            }

            const directIndex = client.children.findIndex(
              (child) => child.id === id
            );

            if (directIndex >= 0) {
              return {
                ...client,
                children: client.children.map((child, i) =>
                  i === directIndex ? data.item : child
                ),
              };
            }

            const groupIndex = client.children.findIndex(
              (child) =>
                Array.isArray(child.children) &&
                child.children.some((gc) => gc.id === id)
            );

            if (groupIndex < 0) {
              return client;
            }

            const tcGroup = client.children[groupIndex];
            const newGroupChildren = tcGroup.children.map((child) =>
              child.id === id ? data.item : child
            );
            const newWarrantySum = newGroupChildren.reduce(getWarrantySum, 0);

            return {
              ...client,
              children: client.children.map((child, i) => {
                if (i !== groupIndex) {
                  return child;
                }

                return {
                  ...child,
                  children: newGroupChildren,
                  department_data: {
                    ...child.department_data,
                    warranty: newWarrantySum,
                  },
                };
              }),
            };
          });

          message.success(data.message);
          return;
        }

        message.error(data.message);
      });
  }

  @action
  private updateExposedFieldInGroup(
    groupId: unknown,
    declarantIds: unknown[],
    entityKey: UpdateDeclarantKeys,
    value: unknown
  ): void {
    this.apiManager
      .updateFieldsInCustomGroup(groupId, {
        [entityKey]: value,
        declarants: declarantIds,
      })
      .then(async (response) => {
        const data = await response.json();

        this.updateExposedDataInGroup(groupId, declarantIds, entityKey, value);
        message.success(data.message);
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.message);
      });
  }

  private updateExposedDataInGroup(
    groupId: unknown,
    declarantIds: unknown[],
    entityKey: string,
    value: unknown
  ): void {
    const newDeclarants = this.customGroupsEntities
      .filter((item) => declarantIds.includes(item.id))
      .map((item) => ({
        ...item,
        [entityKey]: value,
      }));

    this._data = (this._data as Client[]).map((clientRow) => {
      const index = clientRow.children.findIndex((row) => {
        return (
          row.key === groupId &&
          Array.isArray(row.children) &&
          row.children.some((entity) => declarantIds.includes(entity.id))
        );
      });

      if (index < 0) {
        return clientRow;
      }

      return {
        ...clientRow,
        children: clientRow.children.map((row, key) => {
          if (index !== key) {
            return row;
          }

          if (!Array.isArray(row.children)) {
            return row;
          }

          return {
            ...row,
            children: row.children.map((entity) => {
              const replacedItem = newDeclarants.find(
                (newItem) => newItem.id === entity.id
              );

              return replacedItem || entity;
            }),
          };
        }),
      };
    });
  }

  private updateCustomGroup(
    key: string,
    property: string,
    value: unknown
  ): Promise<void> {
    this.isLoadingTableData = true;

    return this.apiManager
      .updateCustomGroupField(key, { [property]: value })
      .then(async (response) => {
        const data = await response.json();

        this._data = this.data.map((entity) => {
          const index = ((entity as Item).children || []).findIndex(
            (entry) => entry.id === data.item.id
          );

          if (index >= 0) {
            const children = [...((entity as Item).children as Array<Item>)];
            children.splice(index, 1, data.item);

            return {
              ...entity,
              children,
            };
          }

          return {
            ...entity,
          };
        });

        this.calculateCustomGroupAttributes();
        message.success(data.message);
      })
      .catch(async (response) => {
        const data = await response.json();

        message.error(data.message);
      })
      .finally(() => {
        this.isLoadingTableData = false;
      });
  }

  private calculateCustomGroupAttributes(): void {
    const customGroupsAttributes: DepartmentDataCustomGroupAttributes[] = [];

    (this._data as Client[]).forEach((entity) => {
      if (!Array.isArray(entity.children) || entity.children.length === 0) {
        return;
      }

      entity.children.forEach((item) => {
        if (Array.isArray(item.department_data)) {
          return;
        }

        if (typeof item.id === "string" && isCustomGroupKey(item.id)) {
          customGroupsAttributes.push(...[item.department_data]);
        }
      });
    });

    this.customGroupsAttributes = customGroupsAttributes;
  }
}

export default DepartmentsDataStore;
